/**
 * Nested groups as nested spheres, packed so that nothing overlaps anything.
 *
 * Grouping asks for something a force layout cannot promise. Pulling a group's members toward a
 * shared centre is a *soft* preference, and non-overlap is a *hard* constraint: nothing in a spring
 * solve forbids a member of one group from settling inside another group's hull, so turning group
 * gravity up tightens each cluster and hardens the collisions at the same time. The 2D
 * force-directed network view has this exact shape of failure.
 *
 * So this is not a force. Every node of the hierarchy - group or component - is a sphere, and a
 * group's sphere is derived from its children's rather than negotiated with them. Non-overlap holds
 * by construction and is checked by `groupPacking.test.ts` rather than judged by eye: no two
 * siblings are closer than the sum of their radii, and every child lies wholly inside its parent.
 *
 * Treating a leaf as a sphere of its own drawn radius is what makes one rule cover both problems.
 * Groups do not overlap groups, and the component marks inside a group do not overlap each other,
 * because the arithmetic never distinguished them.
 *
 * Children sit on a shell rather than filling their parent's volume, which is looser than a packing
 * could be and deliberate: it is what leaves a group's interior free for references to route
 * through. In two dimensions containment and edge routing compete for the same area, which is why
 * grouped 2D graphs congest. Here they are different subspaces - the same division the bundled
 * sphere already makes between its shell of leaves and its interior of bundles.
 *
 * A region is not centred on the mark of the node it belongs to, and the two are returned
 * separately. A group that *is* a component - which `PARENT` and `PARENT_ALL` produce - would
 * otherwise reserve its children's whole extent on one side of that mark and the same clearance
 * again on the other, where there is nothing: a factor per level of containment rather than a term,
 * which over the twenty-one levels a real estate reaches is a region fifty million times the size
 * of a mark. `toUnitCube` fits the region, so the whole view went sub-pixel.
 */

import { toTopDownOrder, type Hierarchy } from '../graph/topology';
import type { Vec3 } from '../types';

/** A region: the space a node and everything under it occupies. */
export type GroupSphere = {
  center: Vec3;
  radius: number;
};

export type GroupPackingArgs = {
  /** The grouping tree. Leaves are components, interior nodes are groups. */
  hierarchy: Hierarchy;
  /** Size of the index space, so the result is parallel to the caller's node list. */
  nodeCount: number;
  /**
   * The drawn radius of each leaf, in layout units.
   *
   * Supplied rather than derived, because what a mark's radius means is the caller's business -
   * here it is only the space that mark must be given. A group's radius is derived; a leaf's is
   * read from this, and a leaf with no entry is a point.
   */
  leafRadii: readonly number[];
  /**
   * Clearance between packed siblings, as a fraction of their combined radii.
   *
   * Not zero. At zero the spheres are exactly tangent, which is correct and reads as a single
   * fused blob - and leaves the non-overlap invariant sitting on floating-point equality, where a
   * rounding error is a failure.
   */
  separation: number;
};

export type GroupPacking = {
  /** Region per index: a leaf's mark, a group's whole extent. */
  spheres: GroupSphere[];
  /**
   * Where each node's own mark goes, which is what a renderer draws it at.
   *
   * Not the centre of `spheres[index]`, and the two parting is what keeps a deep grouping
   * drawable - see the note on nested spheres at the top of this file.
   */
  positions: Vec3[];
};

/**
 * Directions to place `count` siblings in, spread over a sphere.
 *
 * The same Fibonacci construction the bundled sphere places its leaves by, and for the same reason:
 * it spreads points without the pole crowding a latitude-longitude grid produces, so the closest
 * pair of directions is as far apart as a cheap construction gets them - and the closest pair is
 * exactly what sets how large the shell has to be.
 */
const toSiblingDirections = (count: number): Vec3[] => {
  if (count === 1) return [[0, 0, 0]];

  const goldenAngle = Math.PI * (3 - Math.sqrt(5));

  return Array.from({ length: count }, (_, at) => {
    const height = 1 - (2 * at) / (count - 1);
    const ring = Math.sqrt(Math.max(0, 1 - height * height));
    const angle = at * goldenAngle;
    return [Math.cos(angle) * ring, Math.sin(angle) * ring, height] as Vec3;
  });
};

const distance = (left: Vec3, right: Vec3): number =>
  Math.hypot(left[0] - right[0], left[1] - right[1], left[2] - right[2]);

/**
 * The smallest shell that keeps every pair of siblings apart.
 *
 * Closed form rather than grown by trial. Two children placed at `shell * d` are `shell * |di - dj|`
 * apart, and they clear each other when that is at least their combined radii - so the shell is the
 * largest ratio of combined radii to direction distance over all pairs, and solving for it directly
 * is both exact and cheaper than testing candidate radii.
 *
 * A single child needs no shell: it sits at its parent's own centre, and the parent is exactly as
 * large as the child. That is the degenerate case rather than a special one - a group of one adds
 * no structure, and drawing it as a ring around its only member would claim it did.
 */
const toShellRadius = (
  radii: readonly number[],
  directions: readonly Vec3[],
  separation: number
): number => {
  if (radii.length < 2) return 0;

  /**
   * Walked in height order with a bound, rather than over all pairs.
   *
   * The all-pairs form is `k(k-1)/2` distances for a group of `k`, which is fine for the shapes
   * this was written against and is not fine for the shape the default viewpoint produces:
   * `getDefaultViewpoint` groups by workspace, so an estate that mostly lives in one workspace
   * puts everything in one group. Measured on the main thread: 9ms at 1,000 children, 35ms at
   * 2,000, 110ms at 4,000, 451ms at 8,000 - a clean four-fold per doubling, and every rebuild of
   * the layout pays it again.
   *
   * The answer is unchanged. Two points at heights `zi` and `zj` are at least `|zi - zj|` apart,
   * so for a fixed `i` no `j` further away in height than `(ri + rMax)(1 + separation) / shell`
   * can raise the shell - and in height order every later `j` is further still, so the row ends
   * there. Exact rather than an upper bound, which matters: bounding it by the two largest radii
   * over the closest direction pair is sound for non-overlap but inflates a group of uneven marks
   * by up to the full spread of its radii, and uneven is the case the packing exists to get right.
   *
   * Sorted here rather than assumed. `toSiblingDirections` already returns descending height, so
   * the sort is linear-ish on the real input, but a caller passing its own directions should get a
   * correct answer rather than a quietly truncated one.
   */
  const order = radii
    .map((_, index) => index)
    .sort((left, right) => directions[right][2] - directions[left][2]);

  let rMax = 0;
  for (const radius of radii) rMax = Math.max(rMax, radius);

  let shell = 0;
  for (let i = 0; i < order.length; i++) {
    const outer = order[i];
    const height = directions[outer][2];
    const reach = (radii[outer] + rMax) * (1 + separation);

    for (let j = i + 1; j < order.length; j++) {
      const inner = order[j];
      const dropInHeight = height - directions[inner][2];
      /* Nothing below this point can reach, and the rest of the row is further down. */
      if (shell > 0 && dropInHeight > 0 && reach / dropInHeight <= shell) break;

      const needed = (radii[outer] + radii[inner]) * (1 + separation);
      const apart = distance(directions[outer], directions[inner]);
      if (apart > 1e-9) {
        shell = Math.max(shell, needed / apart);
      }
    }
  }
  return shell;
};

const ORIGIN: Vec3 = [0, 0, 0];

/** The axis a group of one arranges itself along, where nothing else decides it. */
const DEFAULT_AXIS: Vec3 = [0, 0, 1];

const along = (direction: Vec3, distance: number): Vec3 => [
  direction[0] * distance,
  direction[1] * distance,
  direction[2] * distance,
];

const toLargest = (radii: readonly number[]): number => {
  let at = 0;
  for (let index = 1; index < radii.length; index++) {
    if (radii[index] > radii[at]) at = index;
  }
  return at;
};

/* Reduced rather than spread: `Math.max(...)` past roughly 125k arguments is a `RangeError` rather
   than a wrong answer, and a workspace group over a large estate reaches that. */
const largestOf = (values: readonly number[]): number =>
  values.reduce((most, value) => Math.max(most, value), 0);

const toSecondLargest = (values: readonly number[]): number => {
  let most = 0;
  let next = 0;
  for (const value of values) {
    if (value > most) {
      next = most;
      most = value;
    } else if (value > next) {
      next = value;
    }
  }
  return next;
};

/**
 * How a node's contents are laid out, in a frame centred on the region rather than on the mark.
 *
 * The distinction is the whole of this module's arithmetic. A group that is also a component - what
 * `PARENT` and `PARENT_ALL` produce - was drawn as a sphere centred on its own mark, which reserves
 * the child's whole region on the far side and the same clearance again on the near side, where
 * there is nothing. About 2.12 times per level of containment, so a chain twenty deep came out
 * millions of times the size of a mark and `toUnitCube` fitted that.
 */
type Arrangement = {
  radius: number;
  /** Where the node's own mark sits, relative to the region's centre. */
  markOffset: Vec3;
  /** Where each child's *region* sits, relative to this region's centre. Parallel to the children. */
  childOffsets: Vec3[];
};

type ArrangementArgs = {
  /** The node's own mark, which is 0 for a group a reader invented. */
  ownRadius: number;
  /** Region radius per child. */
  childRadii: readonly number[];
  separation: number;
};

/**
 * Everything on a shell around the node's own mark. The arrangement this has always made.
 *
 * Children sit on a shell rather than filling their parent's volume, which is looser than a packing
 * could be and deliberate - see the note at the top of this file on why the interior is left free.
 */
const toShellArrangement = ({
  ownRadius,
  childRadii,
  separation,
}: ArrangementArgs): Arrangement => {
  const directions = toSiblingDirections(childRadii.length);
  const shell = Math.max(
    toShellRadius(childRadii, directions, separation),
    ownRadius === 0
      ? 0
      : largestOf(
          childRadii.map(radius => (ownRadius + radius) * (1 + separation))
        )
  );

  return {
    radius: Math.max(
      ownRadius,
      largestOf(childRadii.map(radius => shell + radius))
    ),
    markOffset: ORIGIN,
    childOffsets: directions.map(direction => along(direction, shell)),
  };
};

/**
 * The largest child in the middle, with everything else - the other children and the node's own
 * mark alike - on a shell around it.
 *
 * A shell is the right picture while the children are comparable. It stops being one the moment a
 * child is most of the group: a region the size of the estate cannot sit on a shell without the
 * shell being the size of the estate, so everything else is pushed out past it and the group comes
 * out several times larger than its own largest part. `PARENT_ALL` produces exactly that at the top
 * level, where one deep tree sits among a hundred loose components.
 *
 * `null` unless the largest child is bigger than everything else in the group put together, which
 * is the condition that makes it the group rather than a member of it. Without that test this wins
 * by a percent or two on any large group of equal marks - a flat workspace group would seat one
 * component in the middle of the others for nothing, in the interior the shell exists to keep
 * clear.
 */
const toSeatedArrangement = ({
  ownRadius,
  childRadii,
  separation,
}: ArrangementArgs): Arrangement | null => {
  const seated = toLargest(childRadii);
  const seatedRadius = childRadii[seated];
  const rest = childRadii.map((_, at) => at).filter(at => at !== seated);
  const itemRadii = [
    ...rest.map(at => childRadii[at]),
    ...(ownRadius > 0 ? [ownRadius] : []),
  ];

  /**
   * Ruled out before the shell is solved, where that is possible.
   *
   * `toShellRadius` is the expensive part of a large group - 451ms at 8,000 children - and asking
   * for it twice per node to answer "no" twice as slowly is not worth the arrangement. No two
   * directions are more than two apart, so no shell can come out below this, and the bound settles
   * the comparable-children case without the solve. That is the case a flat workspace group is.
   */
  const restRadius = largestOf(itemRadii);
  const leastRestShell =
    itemRadii.length < 2
      ? 0
      : ((restRadius + toSecondLargest(itemRadii)) * (1 + separation)) / 2;
  if (seatedRadius <= leastRestShell + restRadius) return null;

  /* A lone item has nothing to be spread against, and `toSiblingDirections` answers a group of one
     with the zero vector - which would seat it on top of the child it is meant to clear. */
  const directions =
    itemRadii.length === 1
      ? [DEFAULT_AXIS]
      : toSiblingDirections(itemRadii.length);
  const restShell = toShellRadius(itemRadii, directions, separation);
  if (seatedRadius <= restShell + restRadius) return null;

  const shell = Math.max(
    restShell,
    largestOf(
      itemRadii.map(radius => (seatedRadius + radius) * (1 + separation))
    )
  );

  const childOffsets = new Array<Vec3>(childRadii.length).fill(ORIGIN);
  rest.forEach((at, restAt) => {
    childOffsets[at] = along(directions[restAt], shell);
  });

  return {
    radius: Math.max(
      seatedRadius,
      largestOf(itemRadii.map(radius => shell + radius))
    ),
    markOffset:
      ownRadius > 0 ? along(directions[itemRadii.length - 1], shell) : ORIGIN,
    childOffsets,
  };
};

/**
 * A mark and one child region, enclosed exactly.
 *
 * The degenerate case, and the one a containment chain is made of. Neither of the other two says
 * it well: a shell measures the region from the mark outwards, and seating the child leaves the
 * mark a full clearance away on a shell of its own. The smallest sphere holding two is centred
 * between them and on neither, which is what turns a chain's growth from a factor per level into a
 * term per level.
 */
const toPairArrangement = ({
  ownRadius,
  childRadii,
  separation,
}: ArrangementArgs): Arrangement => {
  const childRadius = childRadii[0];
  const apart = (ownRadius + childRadius) * (1 + separation);
  const low = Math.min(-ownRadius, apart - childRadius);
  const high = Math.max(ownRadius, apart + childRadius);
  const centre = (high + low) / 2;

  return {
    radius: (high - low) / 2,
    markOffset: along(DEFAULT_AXIS, -centre),
    childOffsets: [along(DEFAULT_AXIS, apart - centre)],
  };
};

/**
 * The smallest of the arrangements that can express this node.
 *
 * Decided by measuring rather than by a threshold on how lopsided counts as lopsided. Where the
 * shell is the smaller answer it is chosen, so a grouping of comparable children is packed exactly
 * as it was.
 */
const toArrangement = (args: ArrangementArgs): Arrangement => {
  const seated = toSeatedArrangement(args);
  const candidates = [
    toShellArrangement(args),
    ...(seated === null ? [] : [seated]),
    ...(args.childRadii.length === 1 && args.ownRadius > 0
      ? [toPairArrangement(args)]
      : []),
  ];

  return candidates.reduce((best, candidate) =>
    candidate.radius < best.radius ? candidate : best
  );
};

export const toGroupPacking = ({
  hierarchy,
  nodeCount,
  leafRadii,
  separation,
}: GroupPackingArgs): GroupPacking => {
  const { childIndices, rootIndices } = hierarchy;
  const order = toTopDownOrder(hierarchy);

  const radii = new Array<number>(nodeCount).fill(0);
  /** Where a node's own mark sits, relative to the region it is the centre of. */
  const markOffsets = new Array<Vec3>(nodeCount).fill(ORIGIN);
  /** Where a node's region sits, relative to its parent's region. */
  const centerOffsets = new Array<Vec3>(nodeCount).fill(ORIGIN);

  /* Children before parents, so a group's radius is computed from radii that already exist. */
  for (let at = order.length - 1; at >= 0; at--) {
    const index = order[at];
    const children = childIndices[index];
    const ownRadius = Math.max(0, leafRadii[index] ?? 0);

    if (children.length === 0) {
      radii[index] = ownRadius;
      continue;
    }

    const arrangement = toArrangement({
      ownRadius,
      childRadii: children.map(child => radii[child]),
      separation,
    });

    radii[index] = arrangement.radius;
    markOffsets[index] = arrangement.markOffset;
    children.forEach((child, childAt) => {
      centerOffsets[child] = arrangement.childOffsets[childAt];
    });
  }

  /* The roots are packed the same way, as the children of a sphere nobody draws. */
  const roots = toArrangement({
    ownRadius: 0,
    childRadii: rootIndices.map(index => radii[index]),
    separation,
  });
  rootIndices.forEach((index, at) => {
    centerOffsets[index] = roots.childOffsets[at];
  });

  /* Parents before children, so every offset is added to a centre already resolved. */
  const centers = new Array<Vec3>(nodeCount).fill(ORIGIN);
  rootIndices.forEach(index => {
    centers[index] = centerOffsets[index];
  });
  for (const index of order) {
    const center = centers[index];
    for (const child of childIndices[index]) {
      const offset = centerOffsets[child];
      centers[child] = [
        center[0] + offset[0],
        center[1] + offset[1],
        center[2] + offset[2],
      ];
    }
  }

  return {
    spheres: centers.map((center, index) => ({ center, radius: radii[index] })),
    positions: centers.map((center, index) => {
      const offset = markOffsets[index];
      return [
        center[0] + offset[0],
        center[1] + offset[1],
        center[2] + offset[2],
      ];
    }),
  };
};
